'use client';

import { createClient } from './client';

export interface Country {
  id: string;
  name: string;
  code: string;
  flag: string;
  isActive: boolean;
  articlesCount: number;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

function convertToCountry(row: any): Country {
  return {
    id: row.id,
    name: row.name,
    code: row.code,
    flag: row.flag,
    isActive: row.is_active,
    articlesCount: row.articles_count,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const countryService = {
  async getAll(): Promise<Country[]> {
    try {
      const supabase = createClient();
      if (!supabase) return [];

      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('name');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(convertToCountry) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async create(country: Omit<Country, 'id' | 'createdAt' | 'updatedAt' | 'articlesCount'>): Promise<{ success: boolean; data?: Country; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { data, error } = await supabase
        .from('countries')
        .insert({
          name: country.name,
          code: country.code,
          flag: country.flag,
          is_active: country.isActive,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToCountry(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async update(id: string, country: Partial<Omit<Country, 'id' | 'createdAt' | 'updatedAt' | 'articlesCount'>>): Promise<{ success: boolean; data?: Country; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const updateData: any = {};
      if (country.name !== undefined) updateData.name = country.name;
      if (country.code !== undefined) updateData.code = country.code;
      if (country.flag !== undefined) updateData.flag = country.flag;
      if (country.isActive !== undefined) updateData.is_active = country.isActive;

      const { data, error } = await supabase
        .from('countries')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true, data: convertToCountry(data) };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabase = createClient();
      if (!supabase) return { success: false, error: 'Supabase client not initialized' };

      const { error } = await supabase
        .from('countries')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return { success: false, error: error.message };
        }
      }

      return { success: true };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};